import httpClient from '../../../../shared/services/http/http-client';
import {
  CloudEmissionsProvider,
  CloudResolveAction,
  ICloudCommitResult,
  ICloudConnectionStatus,
  ICloudCoverageSummary,
  ICloudLinesResponse,
  ICloudSendInstructions,
  ICloudSettingsUpdate,
} from './cloud-emissions.model';

export default class CloudEmissionsService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getStatus(provider: CloudEmissionsProvider): Promise<ICloudConnectionStatus> {
    return this.http.get(`cloud-emissions/${provider}/status`).then(response => response.data)
  }

  /** Creates the pending AWS connection and its external id (idempotent). */
  async setupAws(): Promise<ICloudConnectionStatus> {
    return this.http.post('cloud-emissions/aws-sustainability/setup').then(response => response.data)
  }

  /** Emails the role setup instructions to the organisation's AWS admin. */
  async sendAwsInstructions(body: ICloudSendInstructions): Promise<ICloudConnectionStatus> {
    return this.http.post('cloud-emissions/aws-sustainability/send-instructions', body).then(response => response.data)
  }

  /**
   * Tests the role and saves it only if it works. `roleArn` can be whatever the AWS admin sent
   * back (the CloudFormation Outputs row, a sentence): the first role ARN in it is used.
   */
  async connectAws(roleArn: string): Promise<ICloudConnectionStatus> {
    return this.http.post('cloud-emissions/aws-sustainability/connect', { roleArn }).then(response => response.data)
  }

  /** Issues the code the Google Cloud table is labelled with. Idempotent. */
  async setupGcp(): Promise<ICloudConnectionStatus> {
    return this.http.post('cloud-emissions/google-cloud-carbon/setup').then(response => response.data)
  }

  /** Emails the Google Cloud setup instructions to the organisation's Google Cloud admin. */
  async sendGcpInstructions(body: ICloudSendInstructions): Promise<ICloudConnectionStatus> {
    return this.http.post('cloud-emissions/google-cloud-carbon/send-instructions', body).then(response => response.data)
  }

  /**
   * Tests the Carbon Footprint table (access, this organisation's label, the export's columns) and
   * saves it only if every check passes. `table` can be "project.dataset.table", the dataset alone,
   * or a BigQuery console link.
   */
  async connectGcp(table: string): Promise<ICloudConnectionStatus> {
    return this.http.post('cloud-emissions/google-cloud-carbon/connect', { table }).then(response => response.data)
  }

  async refreshScopes(provider: CloudEmissionsProvider): Promise<ICloudConnectionStatus> {
    return this.http.post(`cloud-emissions/${provider}/scopes/refresh`).then(response => response.data)
  }

  async updateSettings(provider: CloudEmissionsProvider, update: ICloudSettingsUpdate): Promise<ICloudConnectionStatus> {
    return this.http.patch(`cloud-emissions/${provider}/settings`, update).then(response => response.data)
  }

  async disconnect(provider: CloudEmissionsProvider): Promise<{ message: string }> {
    return this.http.delete(`cloud-emissions/${provider}/connection`).then(response => response.data)
  }

  async sync(provider: CloudEmissionsProvider, reportPeriodId: string): Promise<{ importId: string, coverageSummary: ICloudCoverageSummary }> {
    return this.http.post(`cloud-emissions/${provider}/sync`, { reportPeriodId }).then(response => response.data)
  }

  async getLines(provider: CloudEmissionsProvider, reportPeriodId?: string): Promise<ICloudLinesResponse> {
    const query = reportPeriodId ? `?reportPeriodId=${reportPeriodId}` : ''
    return this.http.get(`cloud-emissions/${provider}/lines${query}`).then(response => response.data)
  }

  async commit(provider: CloudEmissionsProvider, lineIds: string[]): Promise<ICloudCommitResult> {
    return this.http.post(`cloud-emissions/${provider}/commit`, { lineIds }).then(response => response.data)
  }

  async resolve(
    provider: CloudEmissionsProvider,
    lineId: string,
    body: { action: CloudResolveAction, emissionId?: string | null, excludeEmissionIds?: string[] },
  ): Promise<{ action: string, emissionId?: string | null, excludedEmissionIds?: string[] }> {
    return this.http.post(`cloud-emissions/${provider}/lines/${lineId}/resolve`, body).then(response => response.data)
  }
}
